import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Bookmark, Users, MessageCircle, Eye } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const actionIcons = {
  bookmark: Bookmark,
  going: Users,
  comment: MessageCircle,
  view: Eye
};

const actionTexts = {
  bookmark: "baru bookmark",
  going: "akan pergi ke",
  comment: "komen di",
  view: "tengok"
};

const randomNames = [
  "Ahmad", "Siti", "Muhammad", "Nurul", "Ali", "Fatimah", "Hassan", "Aisyah",
  "Wei Ming", "Mei Ling", "Kumar", "Priya", "Raj", "Sarah", "Daniel", "Lisa"
];

export default function ActivityFeed({ compact = false }) {
  const [activities, setActivities] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    fetchActivities();
    // Reduced polling from 30s to 60s
    const interval = setInterval(fetchActivities, 60000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (activities.length > 0) {
      // Rotate display every 5 seconds (increased from 4s)
      const rotateInterval = setInterval(() => {
        setCurrentIndex(prev => (prev + 1) % Math.min(activities.length, 5));
      }, 5000);
      return () => clearInterval(rotateInterval);
    }
  }, [activities]);

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/analytics/activity/recent?limit=10`);
      setActivities(response.data);
    } catch (error) {
      // Generate mock activities if API fails - silent fail
      const mockActivities = Array(5).fill(null).map((_, i) => ({
        id: i,
        action: ["bookmark", "going", "view"][Math.floor(Math.random() * 3)],
        user_name: randomNames[Math.floor(Math.random() * randomNames.length)],
        event_title: "Event Popular",
        timestamp: new Date().toISOString()
      }));
      setActivities(mockActivities);
    }
  };

  if (activities.length === 0) return null;

  const currentActivity = activities[currentIndex];
  if (!currentActivity) return null;

  const Icon = actionIcons[currentActivity.action] || Eye;
  const actionText = actionTexts[currentActivity.action] || "berinteraksi dengan";

  if (compact) {
    return (
      <div className="fixed bottom-4 right-4 z-40 max-w-xs hidden md:block">
        <AnimatePresence mode="wait">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="glass-card rounded-xl px-4 py-3 border shadow-lg"
          >
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-pink-500/20 flex items-center justify-center">
                <Icon className="h-4 w-4 text-pink-400" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm text-foreground truncate">
                  <span className="font-semibold">{currentActivity.user_name}</span>
                  <span className="text-muted-foreground"> {actionText}</span>
                </p>
                <p className="text-xs text-pink-400 truncate">{currentActivity.event_title}</p>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    );
  }

  return (
    <div className="glass-card rounded-xl p-4 border">
      <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
        <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
        Live Activity
      </h3>
      <div className="space-y-2">
        {activities.slice(0, 5).map((activity, i) => {
          const ActivityIcon = actionIcons[activity.action] || Eye;
          return (
            <motion.div
              key={activity.id || i}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: i * 0.1 }}
              className="flex items-center gap-2 p-2 rounded-lg hover:bg-white/5"
            >
              <div className="w-6 h-6 rounded-full bg-pink-500/20 flex items-center justify-center">
                <ActivityIcon className="h-3 w-3 text-pink-400" />
              </div>
              <p className="text-xs text-muted-foreground flex-1 truncate">
                <span className="text-foreground font-medium">{activity.user_name}</span>
                {" "}{actionTexts[activity.action] || "berinteraksi dengan"}{" "}
                <span className="text-pink-400">{activity.event_title}</span>
              </p>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
